import React, { createContext, useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory } from "react-router-dom";
import { Transition } from "semantic-ui-react";
import { GlobalHotKeys } from "react-hotkeys";
import { selectProjectBySlug } from "../../selectors/projectSelector";
import {
  recordingList,
  recordingUpdate,
  recordingReview,
} from "../../actions/recordingActions";
import { LoaderWithDelay, HotkeyInfo } from "../helpers";
import { BreadcrumbsWithParams } from "../helpers";
import { selectOneRecordingForProject } from "../../selectors/recordingSelector";
import RecordingInfo from "../recording/RecordingInfo";
import { recordingStatuses } from "../../constants";
import RecordingText from "../recording/RecordingText";
import { RecordingNavigationButtons, RecordingCommitButtons } from "../buttons";
import RecorderArea from "../recorder/RecorderArea";
import RecordingComments from "../recording/RecordingComments";
import { commentCreate } from "../../actions/commentAction";

export const RecordingContext = createContext({
  recording: {},
  commentRef: {},
  tgtPlayerRef: {},
  srcPlayerRef: {},
  recBtnRef: {},
  createRecording: () => {},
  updateRecording: () => {},
  changeRecording: () => {},
  reviewRecording: () => {},
});

const RecordingPage = () => {
  // hook values
  const dispatch = useDispatch();
  const history = useHistory();
  const { ownerName, projectSlug, recNum } = useParams();
  const [recording, setRecording] = useState();
  const [transition, setTransition] = useState({
    animation: "fade",
    visible: true,
    duration: 300,
  });
  const [time, setTime] = useState(); //it's used for user audio trick, look inside RecorderAudio
  const commentRef = useRef();
  const tgtPlayerRef = useRef();
  const srcPlayerRef = useRef();
  const recBtnRef = useRef();
  const isRecording = useSelector((state) => state.microphone.isRecording);
  const reviewer = useSelector((state) => state.mode.reviewer);
  const recordings = useSelector((state) => state.recordings);
  const project = useSelector((state) =>
    selectProjectBySlug(state.projects, projectSlug)
  );
  const projFetchError = useSelector((state) => state.projects.error);

  // effects
  useEffect(
    () => {
      if (!project) return;
      dispatch(recordingList(project.id));
    },
    // eslint-disable-next-line
    [project]
  );

  useEffect(
    () => {
      if (!recordings.ids.length) return;
      setRecording(
        selectOneRecordingForProject(recordings, project, parseInt(recNum))
      );
      let animation = "fade";
      if (transition.animation === "fade left") animation = "fade right";
      else if (transition.animation === "fade right") animation = "fade left";
      setTransition({ ...transition, animation: animation, visible: true });
      setTime(new Date().getTime());
    },
    // eslint-disable-next-line
    [recordings, recNum]
  );

  // handlers
  const updateRecording = (newRec) => {
    const comment = commentRef.current.value;
    if (comment) {
      dispatch(commentCreate(newRec.id, comment));
    }
    const userFile = newRec.tmpBlob ? newRec.tmpBlob : null;
    dispatch(recordingUpdate(newRec, userFile));
  };
  const createRecording = (blob) => {
    if (recording.tmpUrl) URL.revokeObjectURL(recording.tmpUrl);
    const tmpUrl = URL.createObjectURL(blob);
    setRecording({ ...recording, tmpUrl: tmpUrl, tmpBlob: blob });
  };
  const reviewRecording = (newStatus) => {
    if (newStatus === recording.reviewer_status) return;
    dispatch(recordingReview(recording.id, newStatus));
    changeRecording("review");
  };
  const changeRecording = (changeTo) => {
    let newNum,
      animation = null;
    switch (changeTo) {
      case "next":
        newNum = recording.nextNum;
        animation = "fade right";
        break;
      case "prev":
        newNum = recording.prevNum;
        animation = "fade left";
        break;
      case "jump":
        newNum = recording.jumpNum;
        animation = "fade left";
        break;
      case "review":
        newNum = recording.reviewNum;
        animation = "fade left";
        break;
      case "skip":
        newNum = recording.jumpNum;
        animation = "fade right";
        break;
      default:
        break;
    }
    setTransition({ ...transition, animation: animation, visible: false });
    let url = newNum
      ? `/${ownerName}/${projectSlug}/recording/${newNum}`
      : `/${ownerName}/${projectSlug}`;
    setTimeout(() => {
      history.push(url);
    }, transition.duration + 50);
  };

  const hotKeyHandlers = {
    NEXT_REC: () => {
      if (!isRecording) changeRecording("next");
    },
    PREV_REC: () => {
      if (!isRecording) changeRecording("prev");
    },
    PLAY_TGT: () => {
      if (!isRecording) playPauseAudio(tgtPlayerRef.current.audioEl);
    },
    PLAY_SRC: () => {
      if (!isRecording) playPauseAudio(srcPlayerRef.current.audioEl);
    },
    RECORD: () => {
      if (!reviewer && recBtnRef.current) recBtnRef.current.ref.current.click();
    },
    DONE: () => {
      if (
        isRecording ||
        !(recording.user_key || recording.tmpUrl) ||
        (recording.user_status === recordingStatuses.DONE && !recording.tmpUrl)
      )
        return;

      updateRecording({ ...recording, user_status: recordingStatuses.DONE });
      changeRecording("jump");
    },
    BAD: () => {
      if (!isRecording) {
        updateRecording({ ...recording, user_status: recordingStatuses.BAD });
        changeRecording(reviewer ? "review" : "jump");
      }
    },
    REDO: () => {
      if (!isRecording && reviewer) reviewRecording(recordingStatuses.REDO);
    },
    APPROVE: () => {
      if (!isRecording && reviewer) reviewRecording(recordingStatuses.APPROVED);
    },
  };

  return (
    <>
      <GlobalHotKeys
        keyMap={keyMap}
        handlers={hotKeyHandlers}
        allowChanges={true}
      />
      {!(project && recording) && !projFetchError.message && (
        <LoaderWithDelay size="massive" content="Fetching data" active />
      )}
      {project && recording && (
        <RecordingContext.Provider
          value={{
            recording,
            commentRef,
            tgtPlayerRef,
            srcPlayerRef,
            recBtnRef,
            createRecording,
            updateRecording,
            changeRecording,
            reviewRecording,
          }}
        >
          <Transition
            animation={transition.animation}
            duration={transition.duration}
            visible={transition.visible}
          >
            <div>
              <BreadcrumbsWithParams />
              <HotkeyInfo />
              <RecordingInfo />
              <RecordingText />
              <RecordingNavigationButtons />
              <RecorderArea time={time} />
              <RecordingCommitButtons />
              <br />
              <br />
              <br />
              <RecordingComments />
            </div>
          </Transition>
        </RecordingContext.Provider>
      )}
    </>
  );
};

const keyMap = {
  NEXT_REC: { name: "Go to next recording", sequence: "d" },
  PREV_REC: { name: "Go to previous recording", sequence: "a" },
  PLAY_TGT: { name: "Play/Pause target recording", sequence: "w" },
  PLAY_SRC: { name: "Play/Pause source recording", sequence: "s" },
  RECORD: { name: "Start/Stop recording process", sequence: "r" },
  DONE: { name: "Save and mark recording as 'Done'", sequence: "e" },
  BAD: { name: "Mark recording as 'Bad'", sequence: "q" },
  REDO: { name: "Review recording and mark to redo", sequence: "c" },
  APPROVE: { name: "Review and approve recording", sequence: "v" },
};

const playPauseAudio = (audio) => {
  if (audio.paused) {
    if (audio.readyState === 4) audio.play();
  } else {
    audio.pause();
  }
};

export default RecordingPage;
